import {
  Box,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Typography,
  Collapse,
} from '@mui/material'
import { useState } from 'react'
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material'
import { useT } from '@/i18n/useT'

interface ModelListEditorProps {
  models: string[]
  selectedModel: string
  onModelsChange: (models: string[]) => void
  onSelectedChange: (model: string) => void
  label: string
  helperText?: string
}

export default function ModelListEditor({
  models,
  selectedModel,
  onModelsChange,
  onSelectedChange,
  label,
  helperText,
}: ModelListEditorProps) {
  const { t } = useT()
  const [newModel, setNewModel] = useState('')
  const [showAddField, setShowAddField] = useState(false)

  const handleAdd = () => {
    const trimmed = newModel.trim()
    if (trimmed && !models.includes(trimmed)) {
      onModelsChange([...models, trimmed])
      setNewModel('')
      setShowAddField(false)
    }
  }

  const handleRemove = (modelToRemove: string) => {
    if (models.length <= 1) return // Don't allow removing the last model
    const updated = models.filter((m) => m !== modelToRemove)
    onModelsChange(updated)
    if (selectedModel === modelToRemove && updated.length > 0) {
      onSelectedChange(updated[0])
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    } else if (e.key === 'Escape') {
      setShowAddField(false)
      setNewModel('')
    }
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
        {label}
      </Typography>

      <TextField
        fullWidth
        select
        size="small"
        value={selectedModel}
        onChange={(e) => onSelectedChange(e.target.value)}
        sx={{ mb: 1 }}
      >
        {models.map((model) => (
          <MenuItem key={model} value={model}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {model}
              </Typography>
              {models.length > 1 && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove(model)
                  }}
                  sx={{ ml: 'auto', flexShrink: 0 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setShowAddField(!showAddField)}
        sx={{ mb: showAddField ? 1 : 0 }}
      >
        {t('Add model')}
      </Button>

      <Collapse in={showAddField}>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            placeholder={`${t('e.g.')} anthropic/claude-sonnet-4.5`}
            value={newModel}
            onChange={(e) => setNewModel(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            autoFocus
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            disabled={!newModel.trim() || models.includes(newModel.trim())}
          >
            {t('Add')}
          </Button>
        </Box>
      </Collapse>

      {helperText && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  )
}
